<script setup>
import { ref } from 'vue';
import { useKanbanStore } from '@/stores/kanban';
import ModalOverlay from './ModalOverlay.vue';

const kanbanStore = useKanbanStore();

const viewDocument = ref(null);
const id = ref(0);
const taskName = ref('');
const isSubmitting = ref(false);
const deleteOption = ref('single');

const openModalDelete = (taskId, name) => {
  id.value = taskId;
  taskName.value = name || '';
  deleteOption.value = 'single';
  if (viewDocument.value) viewDocument.value.openModal();
};

const deleteTask = async (endpoint) => {
  if (isSubmitting.value || !id.value) return;

  isSubmitting.value = true;

  try {
    await kanbanStore.deleteTask(id.value, endpoint);

    if (viewDocument.value) viewDocument.value.closeModal();

    id.value = 0;
    taskName.value = '';
    deleteOption.value = 'single';
  } catch (error) {
    console.error('Erro ao excluir tarefa:', error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleDeleteClick = async () => {
  const endpoints = {
    single: '',
    future: '/future',
    all: '/all'
  };

  const endpoint = endpoints[deleteOption.value] ?? '';
  await deleteTask(endpoint);
};

defineExpose({
  openModalDelete
});
</script>

<template>
  <div>
    <ModalOverlay ref="viewDocument">
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100">
          <h5 class="m-0 font-docs font-weight-bold d-flex align-items-center" style="font-size: 18px; color: #212529;">
            <i class="fa-solid fa-triangle-exclamation text-warning mr-2"></i> Excluir Atividade
          </h5>
          <button
            id="close"
            type="button"
            class="close"
            @click="viewDocument.closeModal()"
            :disabled="isSubmitting"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </template>

      <template #body>
        <div class="font-docs">
          <p class="mb-2" style="font-size: 14px;" v-if="taskName">
            Deseja excluir a atividade <strong>{{ taskName }}</strong>?
          </p>
          <p class="mb-2" style="font-size: 14px;" v-else>
            Deseja excluir esta atividade?
          </p>
          <p class="text-muted mb-3" style="font-size: 13px;">
            Escolha uma das opções abaixo:
          </p>

          <div>
            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'single' }"
              @click="deleteOption = 'single'"
            >
              <input 
                type="radio" 
                id="option-single" 
                value="single" 
                v-model="deleteOption"
              />
              <label for="option-single" style="font-size: 13px;">
                Excluir apenas esta atividade específica
              </label>
            </div>

            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'future' }"
              @click="deleteOption = 'future'"
            >
              <input 
                type="radio" 
                id="option-future" 
                value="future" 
                v-model="deleteOption"
              />
              <label for="option-future" style="font-size: 13px;">
                Excluir esta atividade e todas as futuras da série
              </label>
            </div>

            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'all' }"
              @click="deleteOption = 'all'"
            >
              <input 
                type="radio" 
                id="option-all" 
                value="all" 
                v-model="deleteOption"
              />
              <label for="option-all" style="font-size: 13px;">
                Excluir todas as atividades da série (passadas e futuras)
              </label>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button 
            type="button"
            class="btn btn-outline-secondary-custom font-docs" 
            @click="viewDocument.closeModal()"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button 
            type="button"
            class="btn btn-danger-custom font-docs" 
            @click="handleDeleteClick"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm mr-2"></span>
            {{ isSubmitting ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>

<style scoped>
.font-docs {
  font-family: inherit;
}

.radio-option {
  padding: 10px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.15s ease;
}

.radio-option:hover {
  background-color: #f8f9fa;
}

.radio-option.selected {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.radio-option input[type="radio"] {
  margin-right: 10px;
  accent-color: #dc3545;
  cursor: pointer;
}

.radio-option label {
  cursor: pointer;
  margin: 0;
  color: #212529;
}

.btn-outline-secondary-custom {
  color: #6c757d;
  border: 1px solid #ced4da;
  background: transparent;
  font-weight: 600;
  padding: 6px 18px;
  border-radius: 6px;
}

.btn-danger-custom {
  background-color: #dc3545;
  color: #ffffff;
  border: 1px solid #dc3545;
  font-weight: 600;
  padding: 6px 20px;
  border-radius: 6px;
}
</style>